import { useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Check, Edit } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Customer, useInvoiceQuery, useUpdateCustomerMutation } from '../../../api/gql';
import { FormInput } from '../../../components/form/FormInput';
import { notify } from '../../../utils/notification';
import { useUrlParam } from '../../../utils/urlParams';
import { customerContactValidationSchema } from '../../../utils/validationSchemas';

type CustomerContactForm = Pick<Customer, 'email' | 'phoneNumber'>;

type CustomerContactInput = Pick<Customer, 'name' | 'email' | 'phoneNumber' | 'vatId' | 'id'>;

type CustomerContactProps = CustomerContactInput & { invoiceId: string };

export const CustomerContactForm = ({ invoiceId, name, vatId, id, ...customer }: CustomerContactProps) => {
  const [editParam, setEditParam, clearEditParam] = useUrlParam('customerEdit');
  const { t } = useTranslation('page-invoice-detail');

  const { control, handleSubmit, reset, setFocus, formState, getFieldState } = useForm<CustomerContactForm>({
    defaultValues: customer,
    resolver: yupResolver(customerContactValidationSchema),
  });

  const phoneNumberFieldState = getFieldState('phoneNumber', formState);
  const emailFieldState = getFieldState('email', formState);

  const shouldShowInvalidContactInfoWarning = phoneNumberFieldState.invalid || emailFieldState.invalid;

  const queryClient = useQueryClient();
  const { mutate, isPending } = useUpdateCustomerMutation({
    onSuccess: (data) => {
      queryClient.refetchQueries({
        queryKey: useInvoiceQuery.getKey({ id: invoiceId }),
      });
      clearEditParam();
      reset({ ...data.updateCustomer });
      notify.positive({
        message: t('success'),
      });
    },
    onError: () => {
      notify.negative({
        message: t('error.mutation'),
      });
    },
  });

  const isEditingMode = useMemo(() => editParam() === 'true', [editParam]);
  const sharedInputProps = useMemo(
    () => ({ readOnly: !isEditingMode, disableUnderline: !isEditingMode }),
    [isEditingMode],
  );

  const onSubmit = (data: CustomerContactForm) => {
    if (!vatId || !name) {
      // should never happen
      throw new Error('Customer vatId and name are required fields.');
    }

    mutate({
      customer: {
        id: id,
        vatId: vatId,
        name: name,
        email: data.email,
        phoneNumber: data.phoneNumber,
      },
    });
  };

  const turnOnEditMode = () => {
    setEditParam('true');
    setFocus(Object.keys(customer)[0] as keyof CustomerContactForm);
  };
  const turnOffEditMode = () => {
    clearEditParam();
    reset();
  };

  return (
    <Box>
      <Typography variant={'h4'} mb={2}>
        {name}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={'column'} gap={4}>
          {Object.entries(customer).map(([name]) => (
            <FormInput
              key={name}
              control={control}
              name={name}
              variant={'standard'}
              fullWidth
              InputProps={sharedInputProps}
              label={t(name)}
            />
          ))}

          {shouldShowInvalidContactInfoWarning ? (
            <Alert severity={'error'} sx={{ whiteSpace: 'break-spaces' }}>
              {t('contactInvalidWarning', { ns: 'translation' })}
            </Alert>
          ) : null}

          {formState.isDirty ? (
            <Alert severity={'warning'} sx={{ whiteSpace: 'break-spaces' }}>
              {t('contactChangeWarning', { ns: 'translation' })}
            </Alert>
          ) : null}

          <Box display={'flex'} flexDirection={'column'} gap={2}>
            {isEditingMode ? (
              <LoadingButton
                loading={isPending}
                size={'large'}
                disabled={!formState.isDirty}
                variant={'contained'}
                startIcon={<Check />}
                type={'submit'}
              >
                {t('action.mutate')}
              </LoadingButton>
            ) : null}
            {isEditingMode ? (
              <Button size={'large'} onClick={turnOffEditMode}>
                {t('action.cancel')}
              </Button>
            ) : (
              <Button size={'large'} variant={'outlined'} startIcon={<Edit />} onClick={turnOnEditMode}>
                {t('action.edit')}
              </Button>
            )}
          </Box>
        </Stack>
      </form>
    </Box>
  );
};
